import React, { useState, useEffect } from 'react';
import { pointsAPI } from '../../api';
import { cn, formatRelativeTime } from '../../utils';
import { Card, StatCard, ProgressBar, Spinner, EmptyState } from '../../components/ui';
import { Star, TrendingUp, Flame, Calendar, FileText, ChevronRight } from 'lucide-react';

export const PointsPage = () => {
  const [ledger, setLedger] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [ledgerRes, balanceRes] = await Promise.all([
          pointsAPI.getLedger(100),
          pointsAPI.getBalance()
        ]);
        setLedger(ledgerRes.data);
        setBalance(balanceRes.data);
      } catch (err) {
        console.error('Failed to load points:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="points-page">
      <div>
        <h1 className="text-2xl font-bold">Points Ledger</h1>
        <p className="text-gray-500">Your complete points history with explanations</p>
      </div>

      {/* Balance Summary */}
      {balance && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Star} label="Total Points" value={balance.total_points?.toLocaleString() || 0} color="primary" />
          <StatCard icon={TrendingUp} label="Level" value={`${balance.current_level || 1}`} subtext={balance.level_name} color="blue" />
          <StatCard icon={Flame} label="Current Streak" value={balance.current_streak || 0} subtext="days" color="amber" />
          <StatCard icon={Calendar} label="Sessions" value={balance.sessions_completed || 0} color="purple" />
        </div>
      )}

      {/* XP to Next Level */}
      {balance && balance.xp_to_next_level > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progress to Next Level</span>
            <span className="text-sm text-gray-500">{balance.xp_to_next_level} pts needed</span>
          </div>
          <ProgressBar 
            value={balance.total_points || 0} 
            max={(balance.total_points || 0) + balance.xp_to_next_level} 
          />
        </Card>
      )}

      {/* Ledger Entries */}
      <Card>
        <div className="p-4 border-b">
          <h3 className="font-semibold">Transaction History</h3>
        </div>
        {ledger.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No transactions yet"
            description="Your points activity will appear here"
          />
        ) : (
          <div className="divide-y">
            {ledger.map(entry => (
              <div key={entry.id} className="p-4">
                <div 
                  className="flex items-start justify-between cursor-pointer"
                  onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        'text-lg font-bold',
                        entry.points_delta > 0 ? 'text-green-600' : 'text-red-600'
                      )}>
                        {entry.points_delta > 0 ? '+' : ''}{entry.points_delta}
                      </span>
                      <span className="text-sm font-medium">{entry.rule_name || entry.event_type}</span>
                    </div>
                    <p className="text-sm text-gray-500">{formatRelativeTime(entry.created_at)}</p>
                  </div>
                  <ChevronRight className={cn('w-5 h-5 text-gray-400 transition-transform', expandedId === entry.id && 'rotate-90')} />
                </div>
                
                {expandedId === entry.id && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm space-y-2">
                    <div>
                      <span className="font-medium">Explanation: </span>
                      <span className="text-gray-600">{entry.explanation}</span>
                    </div>
                    {entry.source_session_id && (
                      <div>
                        <span className="font-medium">Session ID: </span>
                        <span className="text-gray-600 font-mono text-xs">{entry.source_session_id}</span>
                      </div>
                    )}
                    <div>
                      <span className="font-medium">Config Version: </span>
                      <span className="text-gray-600">v{entry.config_version}</span>
                    </div>
                    <div>
                      <span className="font-medium">Hash: </span>
                      <span className="text-gray-600 font-mono text-xs">{entry.hash?.slice(0, 16)}...</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default PointsPage;
